import { memo, useEffect, useMemo, useState } from "react";
import { useParentAnalytics } from "@/hooks/useParentAnalytics";
import { trackMetaEvent } from "@/lib/metaPixel";
import { useDashboardScope } from "@/hooks/useDashboardScope";
import { deriveActivityState } from "@/lib/parent/activityState";
import {
  buildPipelineRows,
  buildScorecard,
  buildLearningMix,
  buildMilestones,
  currentPath,
  getCommandStatus,
} from "@/lib/parent/commandMetrics";
import { deriveCurrentPriority } from "@/lib/parent/currentPriority";
import CommandHeader from "../command/CommandHeader";
import KpiStrip from "../command/KpiStrip";
import LearningMomentumCard from "../command/LearningMomentumCard";
import ModulePipeline from "../command/ModulePipeline";
import ReadinessScorecard from "../command/ReadinessScorecard";
import RiskOpportunityPanel from "../command/RiskOpportunityPanel";
import ConsistencyHeatmap from "../command/ConsistencyHeatmap";
import LearningMixDonut from "../command/LearningMixDonut";
import MilestoneTimeline from "../command/MilestoneTimeline";
import MentorPulse from "./intelligence/MentorPulse";
import PrimarySignalPanel from "../command/PrimarySignalPanel";
import RecentActivityTimeline from "./RecentActivityTimeline";
import NextBestParentMove from "./NextBestParentMove";
import ParentBriefing from "./ParentBriefing";
import EngineSignalsPanel from "./EngineSignalsPanel";
import PrimaryParentAction from "../command/PrimaryParentAction";
import { ParentTabBar } from "@/components/parent/shared/parentPrimitives";
import { LayoutGrid, BarChart3, Sparkles as InsightsIcon, ClipboardList } from "lucide-react";

export interface HubChild {
  user_id: string;
  display_name: string;
  age?: number | null;
}

interface Props {
  children: HubChild[];
}

function prettifyChildName(raw: string | null | undefined): string {
  if (!raw) return "Your child";
  const trimmed = raw.trim();
  if (!trimmed) return "Your child";
  const base = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
  const hasSpace = /\s/.test(base);
  const looksFormatted = /[A-Z]/.test(base) && hasSpace;
  if (looksFormatted) return base;
  const tokens = base.split(/[._\-\s]+/).filter(Boolean);
  return tokens.map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()).join(" ");
}

function ParentIntelligenceHubBase({ children }: Props) {
  const [selectedChildId, setSelectedChildId] = useState<string>(children[0]?.user_id ?? "");
  const selectedChild = children.find((c) => c.user_id === selectedChildId) ?? children[0];
  const { scope, isAdmin } = useDashboardScope();
  const [section, setSection] = useState<"overview" | "progress" | "insights" | "action">("overview");

  useEffect(() => {
    if (!selectedChild?.user_id) return;
    void trackMetaEvent("child_progress_viewed", {
      customData: { child_profile_id: selectedChild.user_id, surface: "parent_command_center" },
    });
  }, [selectedChild?.user_id]);

  const { startDate, endDate } = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 90 * 86400000);
    return { startDate, endDate };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional refresh key: dependency exists to force recomputation, not because it is read
  }, [selectedChildId]);

  const analytics = useParentAnalytics({
    childId: selectedChild?.user_id ?? null,
    startDate,
    endDate,
    range: "90d",
  });

  const displayName = prettifyChildName(selectedChild?.display_name);

  const activity = useMemo(() => {
    if (!selectedChild) return null;
    const now = Date.now();
    return deriveActivityState({
      childName: displayName,
      lessonsTotal: analytics.lessonCount,
      milestonesTotal: analytics.lessonActivity.length,
      quizzesTotal: analytics.quizAttempts.length,
      lessonsLast7d: analytics.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 7 * 86400000).length,
      lessonsLast30d: analytics.lessonActivity.filter((l) => now - new Date(l.date).getTime() <= 30 * 86400000).length,
      quizzesLast30d: analytics.quizAttempts.filter((q) => now - new Date(q.completedAt).getTime() <= 30 * 86400000).length,
      daysSinceLastActivity: analytics.daysSinceLastActivity,
    });
  }, [analytics, displayName, selectedChild]);

  const status = useMemo(() => getCommandStatus(analytics, displayName), [analytics, displayName]);
  const pipeline = useMemo(() => buildPipelineRows(analytics), [analytics]);
  const scorecard = useMemo(() => buildScorecard(analytics, pipeline), [analytics, pipeline]);
  const mix = useMemo(() => buildLearningMix(analytics), [analytics]);
  const milestones = useMemo(() => buildMilestones(analytics, pipeline), [analytics, pipeline]);
  const pathLabel = useMemo(() => currentPath(analytics), [analytics]);
  const priority = useMemo(
    () => deriveCurrentPriority({ analytics, pipeline, scorecard, status, pathLabel }),
    [analytics, pipeline, scorecard, status, pathLabel],
  );

  // Budget Builder readiness drives the KPI ring; falls back to overall module readiness for non-beta paths.
  const budget = pipeline.find((p) => p.id === "budget");
  const moduleReadiness = budget ? { percent: budget.readiness, status: budget.status } : { percent: 0, status: "Just Started" };

  if (!selectedChild) return null;

  const betaOnly = false;
  const ageLabel = selectedChild.age != null ? `${selectedChild.age} years old` : null;

  if (analytics.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-24 animate-pulse rounded-3xl border border-white/[0.06] bg-card/40 backdrop-blur-2xl" />
        <div className="h-32 animate-pulse rounded-3xl border border-white/[0.06] bg-card/40 backdrop-blur-2xl" />
        <div className="h-64 animate-pulse rounded-3xl border border-white/[0.06] bg-card/40 backdrop-blur-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="parent-hub-command-center" className="space-y-4 sm:space-y-6">
      {/* 1. Command Center Header */}
      <CommandHeader
        children={children.map((c) => ({ user_id: c.user_id, display_name: prettifyChildName(c.display_name), age: c.age ?? null }))}
        selectedChildId={selectedChildId}
        onSelectChild={setSelectedChildId}
        displayName={displayName}
        ageLabel={ageLabel}
        status={status}
      />

      {/* 2. Sub-tab navigation — internal exploration layer. */}
      <ParentTabBar
        active={section}
        onSelect={(v) => setSection(v as typeof section)}
        items={[
          { value: "overview", label: "Overview", icon: LayoutGrid },
          { value: "progress", label: "Progress", icon: BarChart3 },
          { value: "insights", label: "Insights", icon: InsightsIcon },
          { value: "action", label: "Action Plan", icon: ClipboardList },
        ]}
      />

      {section === "overview" && (
        <div className="space-y-4 sm:space-y-5">
          <PrimarySignalPanel priority={priority} displayName={displayName} analytics={analytics} />
          <KpiStrip analytics={analytics} moduleReadiness={moduleReadiness} status={status} priority={priority} />
          <RecentActivityTimeline analytics={analytics} childName={displayName} />
        </div>
      )}

      {section === "progress" && (
        <div className="space-y-4 sm:space-y-5">
          <div className="grid gap-3 sm:gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <LearningMomentumCard analytics={analytics} insight={activity?.chartTakeaway ?? ""} />
            </div>
            <div className="lg:col-span-2">
              <ReadinessScorecard rows={scorecard} analytics={analytics} />
            </div>
          </div>
          <ModulePipeline rows={pipeline} betaOnly={betaOnly} analytics={analytics} />
          <div className="grid gap-3 sm:gap-4 lg:grid-cols-3">
            <ConsistencyHeatmap analytics={analytics} />
            <LearningMixDonut slices={mix.slices} total={mix.total} />
            <MilestoneTimeline entries={milestones} />
          </div>
        </div>
      )}

      {section === "insights" && (
        <div className="space-y-4 sm:space-y-5">
          <EngineSignalsPanel childId={selectedChild.user_id} childName={displayName} />
          <ParentBriefing
            analytics={analytics}
            childId={selectedChild.user_id}
            childName={displayName}
            periodLabel="last 90 days"
            periodStart={startDate}
            periodEnd={endDate}
          />
          <RiskOpportunityPanel analytics={analytics} scorecard={scorecard} status={status} />
          <MentorPulse analytics={analytics} priority={priority} />
        </div>
      )}

      {section === "action" && (
        <div className="space-y-4 sm:space-y-5">
          <NextBestParentMove priority={priority} analytics={analytics} displayName={displayName} />
          <PrimaryParentAction childId={selectedChild.user_id} analytics={analytics} status={status} priority={priority} />
        </div>
      )}

      <p className="px-2 pt-2 text-center text-[11px] italic text-muted-foreground">
        Every session builds tomorrow's confidence.
      </p>
    </div>
  );
}

const ParentIntelligenceHub = memo(ParentIntelligenceHubBase);
export default ParentIntelligenceHub;
